import React, { useState, useEffect} from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import { SpinnerCircular } from "spinners-react";

import TextField from "@mui/material/TextField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import useHttp from "../../../hooks/useHttp";
import "../../../assets/css/utility/input-field.css";
import "../../../assets/css/user_auth_pages.css";
import Styles from "./Login.module.css";
import SuccessMessage from "../../../components/SuccessMessage";
import { RESET_PASSWORD_URL } from "../../../utils/api-ends";
import { gsap } from "gsap";

const ResetPassword = ({ authContainer }) => {
  let reset_pass_tl = gsap.timeline();

  const [step, setStep] = useState(1); // We will have two step, first step the toke and second step passwords. Will send API call together !
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationCodeError, setVerificationCodeError] = useState([]);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState([]);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState([]);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const { error, isLoading, sendRequests } = useHttp();
  const navigate = useNavigate();

  const onVerificationCodeChange = (e) => setVerificationCode(e.target.value);
  const onPasswordChange = (e) => setPassword(e.target.value);
  const onConfirmPasswordChange = (e) => setConfirmPassword(e.target.value);

  const viewPassword = () => setShowPassword(!showPassword);
  const viewConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const validateFirstStepData = () => {
    // Empty error messages
    setVerificationCodeError([]);
    let hasError = false;

    if (verificationCode?.trim()?.length === 0) {
      setVerificationCodeError(["Verification code is required"]);
      hasError = true;
    }
    return hasError;
  };
  const validateSecondStepData = () => {
    if (validateFirstStepData()) {
      setStep(1);
      return;
    }

    // Empty error messages
    setPasswordError([]);
    setConfirmPasswordError([]);
    let hasError = false;

    if (password?.trim()?.length === 0) {
      setPasswordError(["New password is required"]);
      hasError = true;
    }
    if (confirmPassword?.trim()?.length === 0) {
      setConfirmPasswordError(["Confirm password is required"]);
      hasError = true;
    }
    if (password && confirmPassword && password !== confirmPassword) {
      setPasswordError(["Passwords do not match"]);
      hasError = true;
    }
    return hasError;
  };

  const onSubmit = () => {
    if (isLoading) return false;
    if (validateFirstStepData()) return;

    if (step === 1) {
      setStep(2);
      // for animation
      reset_pass_tl
        .to(authContainer.current, { x: -1000, opacity: 0, duration: 0.1 })
        .to(authContainer.current, { x: 1000, duration: 0.1 })
        .to(authContainer.current, { x: 0, opacity: 1, duration: 0.1 });
      return; // breaking the execution.
    }

    if (validateSecondStepData()) return;

    const response = (data) => {
      setSuccessMsg(data.message);

      setTimeout(() => {
        navigate("/auth/sign-in");
      }, 700);
    };

    sendRequests(
      {
        url: RESET_PASSWORD_URL,
        method: "POST",
        data: {
          verification_key: verificationCode,
          new_password: password,
          confirm_new_password: confirmPassword,
        },
      },
      response
    );
  };

  const reset_pass_back_btn = () => {
    setStep(1);
    // for animation
    reset_pass_tl
      .to(authContainer.current, { x: 1000, opacity: 0, duration: 0.1 })
      .to(authContainer.current, { x: -1000, duration: 0.1 })
      .to(authContainer.current, { x: 0, opacity: 1, duration: 0.1 });
  };

  useEffect(() => {
    if (error) {
      if (error.new_password) {
        setPasswordError(error.new_password);
      }
      if (error.verification_key) {
        setVerificationCodeError(error.verification_key);
        reset_pass_back_btn();
      }
      if (error.general) {
        setVerificationCodeError(error.general);
        reset_pass_back_btn();
      }
    }
  }, [error]);

  return (
    <>
      <p className="auth-instruction"></p>

      <div className="user-input-field">
        {successMsg && <SuccessMessage message={successMsg} />}

        {step === 1 ? (
          <div className="input-box-area">
            {verificationCodeError?.map((error, index) => (
              <p className="auth-error" key={index}>
                {error}
              </p>
            ))}

            <TextField
              fullWidth
              autoFocus
              className="the-mui-input-field"
              onChange={onVerificationCodeChange}
              label="Verification Code"
              variant="outlined"
              value={verificationCode}
            />
          </div>
        ) : (
          /////////////////////
          // First step ends//
          /////////////////////
          ///////////////////////
          // Second step starts//
          ///////////////////////
          <div>
            <div className="input-box-area">
              {passwordError?.map((error, index) => (
                <p className="auth-error" key={index}>
                  {error}
                </p>
              ))}

              <TextField
                fullWidth
                autoFocus
                className="the-mui-input-field"
                onChange={onPasswordChange}
                label="New Password"
                variant="outlined"
                type={showPassword ? "text" : "password"}
                value={password}
              />
              <span onClick={viewPassword}>
                <FontAwesomeIcon
                  icon={`fa-solid ${showPassword ? "fa-eye" : "fa-eye-slash"}`}
                />
              </span>
            </div>

            <div className="input-box-area">
              {confirmPasswordError?.map((error, index) => (
                <p className="auth-error" key={index}>
                  {error}
                </p>
              ))}

              <TextField
                fullWidth
                className="the-mui-input-field"
                onChange={onConfirmPasswordChange}
                label="Confirm New Password"
                variant="outlined"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
              />
              <span onClick={viewConfirmPassword}>
                <FontAwesomeIcon
                  icon={`fa-solid ${
                    showConfirmPassword ? "fa-eye" : "fa-eye-slash"
                  }`}
                />
              </span>
            </div>
          </div>
        )}

        <div className={Styles.ButtonContainer}>
          {step === 2 && (
            <Button
              onClick={reset_pass_back_btn}
              className={`theme-btn submit-btn ${Styles.BackButton}`}
            >
              <FontAwesomeIcon
                icon="fa-solid fa-angles-left"
                className={Styles.BackIcon}
              />
              Back
            </Button>
          )}

          <Button className="theme-btn submit-btn" onClick={onSubmit}>
            {step === 1 ? "Next" : "Submit"}
            {isLoading && (
              <SpinnerCircular
                size={22}
                thickness={180}
                speed={130}
                color="#fff"
                className="btn-loader"
                secondaryColor="rgba(57, 172, 127, 0.44)"
              />
            )}
          </Button>
        </div>
      </div>

      <Link to="/auth/forget-password" className="auth-usefull-links">
        Re-type forgot password email !
      </Link>

      <Link to="/auth/sign-in" className="auth-usefull-links">
        Sign In
      </Link>
    </>
  );
};

export default ResetPassword;
